import { z, type ZodType } from 'zod';
import type { ObjectToMarkdownFormat } from './object';

/**
 * Structured-output schema registry.
 *
 * Entry ids are `{app}.{name}@{version}` (e.g. `uvilo-ai.coaching-analysis@1`).
 * Old versions stay registered so historical JSON rows remain readable.
 */

/** Markdown rendering map for registered payloads (key order + formatters). */
export type SchemaFormat = ObjectToMarkdownFormat;

export type SchemaEntry = {
  readonly id: string;
  readonly app: string;
  readonly name: string;
  readonly version: number;
  readonly zodSchema: ZodType;
  readonly jsonSchema: Record<string, unknown>;
  /** Markdown rendering map for the payload. */
  readonly format: SchemaFormat;
};

const schemas: SchemaEntry[] = [];
const schemasById = new Map<string, SchemaEntry>();

/**
 * Converts a Zod schema to a plain JSON Schema object for LLM structured outputs.
 * @param schema Zod schema to convert.
 * @returns JSON Schema without `$schema`.
 */
function zodSchemaToJsonSchema(schema: ZodType): Record<string, unknown> {
  const jsonSchema = z.toJSONSchema(schema, { reused: 'inline' }) as Record<string, unknown>;
  delete jsonSchema['$schema'];
  return jsonSchema;
}

/**
 * Builds a schema registry id.
 * @param app Owning application (`uvilo-ai` or `forge`).
 * @param name Logical schema name (e.g. `coaching-analysis`).
 * @param version Integer schema version.
 * @returns Registry id `app.name@version`.
 */
export function schemaId(app: string, name: string, version: number): string {
  return `${app}.${name}@${version}`;
}

/**
 * Builds the display label for a registered schema (`app: name vN`).
 * @param schema Registry entry (or the app/name/version fields).
 * @returns Display label.
 */
export function formatSchemaLabel(schema: Pick<SchemaEntry, 'app' | 'name' | 'version'>): string {
  return `${schema.app}: ${schema.name} v${schema.version}`;
}

/**
 * Registers a structured-output schema. Converts `zodSchema` to JSON Schema at registration.
 * Replaces an existing entry with the same id (HMR-safe).
 * @param entry Registration fields; `id` and `jsonSchema` are derived.
 * @param entry.app Owning application (`uvilo-ai` or `forge`).
 * @param entry.name Logical schema name (e.g. `coaching-analysis`).
 * @param entry.version Integer schema version.
 * @param entry.zodSchema Zod contract for the payload.
 * @param entry.format Markdown rendering map for the payload.
 * @returns The registered entry (with derived `id` and `jsonSchema`).
 */
export function registerSchema(entry: {
  app: string;
  name: string;
  version: number;
  zodSchema: ZodType;
  format: SchemaFormat;
}): SchemaEntry {
  const id = schemaId(entry.app, entry.name, entry.version);
  const registered: SchemaEntry = {
    id,
    app: entry.app,
    name: entry.name,
    version: entry.version,
    zodSchema: entry.zodSchema,
    jsonSchema: zodSchemaToJsonSchema(entry.zodSchema),
    format: entry.format,
  };

  const existingIndex = schemas.findIndex((schema) => schema.id === id);
  if (existingIndex >= 0) {
    schemas[existingIndex] = registered;
  } else {
    schemas.push(registered);
  }
  schemasById.set(id, registered);
  return registered;
}

/**
 * Looks up a schema by registry id.
 * @param id Schema id (`app.name@version`).
 * @returns The entry, or undefined.
 */
export function getSchema(id: string): SchemaEntry | undefined {
  return schemasById.get(id);
}

/**
 * Returns the latest registered version of a named schema for an application.
 * @param app Owning application (`uvilo-ai` or `forge`).
 * @param name Logical schema name.
 * @returns Latest entry, or undefined.
 */
export function getLatestSchema(app: string, name: string): SchemaEntry | undefined {
  let latest: SchemaEntry | undefined;
  for (const schema of schemas) {
    if (schema.app !== app || schema.name !== name) continue;
    if (!latest || schema.version > latest.version) {
      latest = schema;
    }
  }
  return latest;
}

/**
 * Lists the latest version of each schema name across all apps (cross-app Editor dropdown).
 * Keyed by `app.name` so the same logical name from two apps both appear.
 * @returns Latest entry per app+name, sorted by app then name.
 */
export function listSchemas(): SchemaEntry[] {
  const latestByAppAndName = new Map<string, SchemaEntry>();
  for (const schema of schemas) {
    const key = `${schema.app}\0${schema.name}`;
    const current = latestByAppAndName.get(key);
    if (!current || schema.version > current.version) {
      latestByAppAndName.set(key, schema);
    }
  }
  return [...latestByAppAndName.values()].sort((a, b) => {
    const appCompare = a.app.localeCompare(b.app);
    return appCompare !== 0 ? appCompare : a.name.localeCompare(b.name);
  });
}

/**
 * Returns all registered schemas (for tests / diagnostics).
 * @returns Snapshot of registered entries.
 */
export function getAllSchemas(): readonly SchemaEntry[] {
  return schemas;
}
